<template>
    <div class="mt-5">
        <delete-confirmation name="document" @confirm="deleteDocument"></delete-confirmation>
        <div :class="[{'border-bottom': collapsed}, 'd-flex justify-content-between align-items-end']">
            <div class="h4">
                <a href="#documentsTable" class="mr-2" role="button" @click="collapsed = !collapsed">
                    <i :class="['fas text-muted', collapsed ? 'fa-caret-right' : 'fa-caret-down']"></i>
                </a>
                {{ title }} 
                <span v-if="subtitle" class="font-xs font-italic text-muted">({{ subtitle }})</span>          
            </div>

            <div v-if="!collapsed" class="btn-toolbar" role="toolbar">
                <div class="input-group input-group-sm mb-1">
                    <div class="input-group-prepend">
                        <span class="input-group-text fas fa-search fa-sm text-muted"></span>                       
                    </div>
                    <input type="text" class="form-control" placeholder="Search Descriptions" :size="searchInputSize" v-model="searchString">
                </div>
                <div class="btn-group ml-2"> 
                    <button type="button" class="btn btn-primary btn-sm mb-1" data-toggle="tooltip" title="Filter" @click="showFilters()">
                        <i class="fas fa-filter fa-lg"></i>
                    </button>
                </div>
            </div>
        </div>
        <div id="documentsTable" :class="['collapse', { 'show': !collapsed }]">
            <div v-if="filteredDocuments.length === 0" class="border-top"><h3 class="text-muted text-center mt-3">No Documents.</h3></div>           
            <table v-else class="table table-sm">
                <thead>
                    <tr>
                        <th></th>
                        <th class="fit">Document Type</th>
                        <th>Description</th>
                        <th class="fit">Owner</th>
                        <th class="fit">Uploaded/Received</th>
                        <th v-if="showSubmittedAt" class="fit">Submitted</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="doc in filteredDocuments">
                        <tr :key="'researcher-' + (doc.historical ? 'historical-' : '') + documentType + '-' + doc.id">
                            <td class="fit px-0">
                                <div class="d-flex justify-content-start align-items-center">
                                    <div><i :class="['fas fa-sm fa-fw', {'fa-link text-muted mb-1': doc.is_linked}]"></i></div>
                                    <div>                                                                        
                                        <document-status-icon v-if="doc.document_status" :document-id="doc.id" :document-status="doc.document_status"></document-status-icon>
                                        <span v-else-if="doc.status_count" class="fa-stack fa-xs">
                                            <i class="fas fa-circle fa-stack-2x fa-fw text-muted"></i>
                                            <span class="fa-stack-1x font-weight-bold text-white">{{ doc.status_count }}</span>
                                        </span>
                                        <i v-else :class="['fas fa-lg fa-fw', {'fa-history text-muted': doc.historical}]"></i> 
                                    </div>
                                </div>                                                              
                            </td>
                            <td class="fit">                            
                                <document-type-icon :document="doc" :href="getDocumentUrl(doc)"></document-type-icon>                                                                              
                            </td>
                            <td>
                                <input v-if="doc.edit" class="form-control form-control-sm" v-model="doc.description" @blur="changeDescription(doc)" @keyup.enter="changeDescription(doc)" v-focus>
                                <span v-else @click="doc.edit = true;" :title="doc.description" class="text-truncate">                                
                                    {{ doc.description | truncate(80) }}
                                    <a href="javascript:void(0)" class="edit-button"><i class="fas fa-pencil-alt fa-xs"></i></a>
                                </span>
                            </td>
                            <td class="fit pr-3">{{ doc.owner }}</td>
                            <td class="fit pr-3">{{ doc.created_at | localizeTime('YYYY-MM-DD HH:mm:ss') }}</td>
                            <td v-if="showSubmittedAt" class="fit pr-3">
                                <span v-if="doc.historical">{{ doc.submit_date }}</span>
                                <span v-else>{{ doc.submitted_at | localizeTime('YYYY-MM-DD HH:mm:ss') }}</span>
                            </td>
                            <td class="fit">
                                <div class="d-flex flex-nowrap flex-row justify-content-end">

                                    <!-- delete document -->
                                    <div class="text-center">
                                        <button v-if="doc.can_delete" class="btn btn-link px-1 py-0" title="Delete Document" @click="confirmDelete(doc)">
                                            <i class="fas fa-trash-alt fa-lg fa-fw"></i>
                                        </button>
                                        <button v-else-if="doc.can_unlink_rms" class="btn btn-link p-0" title="Unlink from Claim" @click="confirmDissociate(doc)">
                                            <i class="fas fa-unlink fa-md fa-fw"></i>
                                        </button>
                                        <i v-else class="fas fa-md fa-fw"></i>                                                                               
                                    </div>                                                                  

                                    <!-- associated document to CMS document -->
                                    <div class="text-center">
                                        <button class="btn btn-link px-1 py-0" title="Link to CMS Document(s)" @click="associate(doc)">
                                            <i class="fas fa-link fa-md fa-fw"></i>
                                        </button>                                        
                                    </div>                                  

                                </div>
                            </td>
                        </tr>
                    </template>                  
                </tbody>
            </table>
        </div>      
    </div>    
</template>

<script>
import { DocumentType, HistoricalState } from '../../constants.js';
import DocumentStatusIcon from '../documents/DocumentStatusIconComponent.vue';
import DocumentTypeIcon from '../documents/DocumentTypeIconComponent.vue';

export default {
    components: {
        DocumentStatusIcon,
        DocumentTypeIcon
    },
    props: {
        clientId: Number,
        claimId: Number,
        title: String,
        subtitle: String,
        href: String,
        state: Number
    },
    data() {
        return {
            collapsed: false,
            searchInputSize: 30,
            searchString: '',
            filters: {
                link_status: 'all'
            },
            loading: false,
            documents: [],

            DocumentType: DocumentType,
            HistoricalState: HistoricalState
        }
    },    
    computed: {
        documentType: function () {
            return this.href.substring(this.href.lastIndexOf('/') + 1);
        },
        showSubmittedAt: function ()
        {
            return ['authorizations', 'disputes'].includes(this.documentType);
        },     
        filteredDocuments: function () {

            let self = this;
            let retval = self.documents;         

            // filter on historical state
            if (self.state !== HistoricalState.ALL) {
                retval = retval.filter(x => x.historical === (self.state === HistoricalState.ONLY));
            }             

            // filter on description
            retval = retval.filter(function (doc) {
                return (doc.description.toLowerCase().indexOf(self.searchString.toLowerCase()) !== -1);
            });             

            // filter on linked status
            if (self.filters.link_status && self.filters.link_status !== 'all') {

                retval = retval.filter(e => {
                    return (self.filters.link_status === 'linked') ? e.is_linked : !e.is_linked;
                });
            }            
            
            return retval;
        }       
    },
    methods: {
        refresh: function (reset) {
            let self = this;
            self.loading = true;

            axios.get(self.href)
                .then(response => {

                    // reset the document section collapse state (user preferences)
                    if (reset) {
                        self.collapsed = response.data.collapsed;
                    }

                    // set the description edit flag on each document 
                    self.documents = response.data.documents.map(e => {
                        self.$set(e, 'edit', false);
                        return e;
                    });

                    // enable popovers after the DOM has updated
                    self.$nextTick(() => {
                        $('[data-toggle="popover"]').popover();                      
                    });                    

                    self.loading = false;
                })
                .catch(error => {
                    console.log(error);
                    self.loading = false;
                });

        },       
        showFilters: function () {
            eventHub.$emit('show-researcher-document-filters', this.documentType, this.filters, this.filter);
        },
        filter: function (filters) {
            this.filters = filters;
        },
        changeDescription: function (document) {

            document.edit = false;

            // update the model
            this.$emit('update');

            // send the change to the server
            if (document.historical) {

                axios.patch(`/ajax/clients/${this.clientId}/claims/${this.claimId}/historical/documents/${document.id}`, {
                    key: 'description',
                    value: document.description
                });
            }
            else {

                axios.patch(`/ajax/documents/${document.id}/description`, {
                    'description': document.description
                });
            }       
        }, 
        associate: function (document) {
            eventHub.$emit('show-cms-document-association', document, this.state);
        },
        confirmDissociate: function (document) {
            const url = `/ajax/responses/${document.id}/disassociate`;
            const message = '' +
                '<h5 class="font-weight-bold text-center"><i class="fas fa-exclamation-triangle text-warning"></i> WARNING</h5>' +
                '<div class="font-weight-bold">' +
                'Unlinking the document will return it to the RMS Documents list. Are you sure you want to unlink the document from this claim?' +
                '</div>';
            eventHub.$emit('show-disassociate-confirmation', url, null, message);
        },
        confirmDelete: function (document) {
            eventHub.$emit('show-delete-document-confirmation', document);
        },
        deleteDocument: function (document) {

            let url = document.historical ?
                `/ajax/clients/${this.clientId}/claims/${this.claimId}/historical/documents/${document.id}` :
                `/ajax/documents/${document.id}`;

            axios.delete(url)
                .then(response => {
                    // nothing to do; server side event will cause refresh
                })
                .catch(error => {
                    console.log(error);
                }); 
        },
        getDocumentUrl: function (document) {
            return document.historical ?
                `/clients/${this.clientId}/claims/${this.claimId}/historical/documents/${document.id}` :
                `/claims/${this.claimId}/documents/${document.id}`;
        },
        markRead: function (document) {

        }                
    },
    created() {

        this.refresh(true);

        // listen for laravel echo events
        window.Echo.private('claims.' + this.claimId)
            .listen('DocumentEvent', (e) => {
                this.refresh(false); 
            })
            .listen('DocumentReadEvent', (e) => {
                this.markRead(e.document);
            });        
    },
    directives: {
        focus: {
            inserted: function (el) {
                el.focus();
            }
        }
    }    
}
</script>

<style scoped>
    .font-sm {
        font-size: 0.9em;
    }

    .table-sm td {
        padding-top: 0.25rem !important;
        padding-bottom: 0.25rem !important;
        vertical-align: middle;
    }

    .font-xs {
        font-size: 0.6em;
    }

    .input-group {
        align-self: flex-start;
    }

    .edit-button {
        position: relative;
        top: -4px;
        opacity: 0;
    }

    .edit-button:hover {
        opacity: 1;
    }     
</style>